/*
  背景の明るさ（サイト共通）

  「この絵の、この範囲は暗いか」を返すだけの道具。
  白い文字を乗せてよいかを決めるのに使う。

  使っているところ
  - TECHNOLOGYの見出し（assets/custom tech hero.js）
  - 商品詳細の枚数と目盛り（assets/custom product story.js）
  - グローバルヘッダー（assets/custom header.js）

  実装仕様
  #1 キャンバスに描いて読み出すので、画面に出ている img とは別に
     crossOrigin を付けた小さい画像を読み直す。画面の img は crossOrigin 無しで
     読み込まれていて、そのままではキャンバスが汚れて読み出せない。
     Shopifyの画像URLは width で小さい版を取れるので通信量はごくわずか
  #2 1枚につき「24列 x 16行の明るさの表」を1回だけ作って覚える。
     ヘッダーのように、スクロールで絵の中のどこが下に来るかが変わる使い方では、
     測り直しが毎フレーム起きる。表にしておけば以降は算術で引くだけになる
  #3 読み出せなかったときは全部 1（＝明るい）にする。
     白が消えるより、濃い文字が濃い地に乗るほうが被害が小さい……のではなく、
     このサイトでは地が白のページが大多数なので、外したときに読めるのは濃い側
  #4 グリッドの座標は「絵そのもの」の 0〜1。画面に出ている範囲は
     object fit cover で切り取られているので、cover() で先に読み替える
  #5 グローバルを1つだけ生やす（window.cLuminance）。
     このサイトのJSはどれも即時関数で閉じているが、3箇所で同じ計算を
     持つほうが害が大きいので、ここだけ例外にしている
*/
(function () {
  'use strict';

  /*
    白と紺の境目。TECHNOLOGYの見出しで決めた値を、そのままサイト共通にしている。
    ここを動かすと3箇所の見え方が同時に変わる
  */
  var LIGHT_LIMIT = 0.55;

  /* 明るさの表の細かさ。ヘッダーの項目1つが2〜4列に当たるくらい */
  var COLS = 24;
  var ROWS = 16;

  /* 読み直す画像の幅。表より細かければ足りる */
  var PROBE_WIDTH = 96;

  /* src をキーに覚える。失効も上限も持たない（1ページで数枚しか測らない） */
  var CACHE = {};
  var PENDING = {};

  function sampleSrc(src) {
    return src.replace(/([?&])width=\d+/, '$1width=' + PROBE_WIDTH);
  }

  /* 読み出せなかったとき用の、全部明るい表 */
  function blank() {
    var cells = [];

    for (var i = 0; i < COLS * ROWS; i++) {
      cells.push(1);
    }

    return { cells: cells, cols: COLS, rows: ROWS, aspect: 1, ok: false };
  }

  /* 1枚を 24 x 16 の明るさの表にする。実装仕様 #2 */
  function build(probe) {
    var nw = probe.naturalWidth || 0;
    var nh = probe.naturalHeight || 0;

    if (!nw || !nh) {
      return blank();
    }

    var canvas = document.createElement('canvas');

    canvas.width = nw;
    canvas.height = nh;

    try {
      var ctx = canvas.getContext('2d');
      ctx.drawImage(probe, 0, 0);

      var data = ctx.getImageData(0, 0, nw, nh).data;
      var sums = [];
      var counts = [];
      var i;

      for (i = 0; i < COLS * ROWS; i++) {
        sums.push(0);
        counts.push(0);
      }

      for (var y = 0; y < nh; y++) {
        var row = Math.min(ROWS - 1, Math.floor((y / nh) * ROWS));

        for (var x = 0; x < nw; x++) {
          var col = Math.min(COLS - 1, Math.floor((x / nw) * COLS));
          var at = (y * nw + x) * 4;
          /* Rec.709 の相対輝度を 0〜1 に正規化する。透明度は見ない */
          var value = (data[at] * 0.2126 + data[at + 1] * 0.7152 + data[at + 2] * 0.0722) / 255;

          sums[row * COLS + col] += value;
          counts[row * COLS + col] += 1;
        }
      }

      var cells = [];

      for (i = 0; i < COLS * ROWS; i++) {
        cells.push(counts[i] ? sums[i] / counts[i] : 1);
      }

      return { cells: cells, cols: COLS, rows: ROWS, aspect: nw / nh, ok: true };
    } catch (error) {
      /* キャンバスが汚れて読み出せない。実装仕様 #3 */
      return blank();
    }
  }

  /*
    明るさの表を用意する。
    覚えていればその場で、まだなら読み込んでから done を呼ぶ。
    同じ絵を同時に何度頼まれても、読み直すのは1回だけ
  */
  function grid(src, done) {
    if (!src) {
      done(blank());
      return;
    }

    if (Object.prototype.hasOwnProperty.call(CACHE, src)) {
      done(CACHE[src]);
      return;
    }

    if (PENDING[src]) {
      PENDING[src].push(done);
      return;
    }

    PENDING[src] = [done];

    var probe = new Image();
    probe.crossOrigin = 'anonymous';

    function finish(value) {
      CACHE[src] = value;

      var waiting = PENDING[src] || [];
      delete PENDING[src];

      waiting.forEach(function (fn) {
        fn(value);
      });
    }

    probe.onload = function () {
      finish(build(probe));
    };

    probe.onerror = function () {
      finish(blank());
    };

    probe.src = sampleSrc(src);
  }

  /* 覚えている表。まだ測っていなければ null（呼ぶ側が grid で頼む） */
  function known(src) {
    return Object.prototype.hasOwnProperty.call(CACHE, src) ? CACHE[src] : null;
  }

  /*
    表の一部の平均。範囲は絵そのものの 0〜1（実装仕様 #4）。
    1マスにも満たない範囲を頼まれても、必ず1マスは読む
  */
  function average(table, x0, y0, x1, y1) {
    if (!table) {
      return 1;
    }

    var cols = table.cols;
    var rows = table.rows;
    var left = Math.max(0, Math.min(cols - 1, Math.floor(Math.min(x0, x1) * cols)));
    var right = Math.max(left, Math.min(cols - 1, Math.ceil(Math.max(x0, x1) * cols) - 1));
    var top = Math.max(0, Math.min(rows - 1, Math.floor(Math.min(y0, y1) * rows)));
    var bottom = Math.max(top, Math.min(rows - 1, Math.ceil(Math.max(y0, y1) * rows) - 1));
    var total = 0;
    var count = 0;

    for (var row = top; row <= bottom; row++) {
      for (var col = left; col <= right; col++) {
        total += table.cells[row * cols + col];
        count += 1;
      }
    }

    return count ? total / count : 1;
  }

  /*
    object fit cover で切り取られたあと、絵のどこが見えているか（0〜1）。実装仕様 #4

    窓のほうが横長なら左右が、縦長なら上下が切られる。どちらも中央合わせ。
    呼ぶ側は「窓の中のこの範囲」を、この戻り値で「絵の中のこの範囲」に読み替える
  */
  function cover(imageAspect, boxAspect) {
    if (!imageAspect || !boxAspect) {
      return { x: 0, y: 0, w: 1, h: 1 };
    }

    if (imageAspect > boxAspect) {
      var w = boxAspect / imageAspect;
      return { x: (1 - w) / 2, y: 0, w: w, h: 1 };
    }

    var h = imageAspect / boxAspect;
    return { x: 0, y: (1 - h) / 2, w: 1, h: h };
  }

  /* 窓の中の範囲（0〜1）を、絵の中の範囲（0〜1）に読み替える */
  function inBox(table, boxAspect, x0, y0, x1, y1) {
    var area = cover(table ? table.aspect : 1, boxAspect);

    return average(
      table,
      area.x + area.w * x0,
      area.y + area.h * y0,
      area.x + area.w * x1,
      area.y + area.h * y1
    );
  }

  window.cLuminance = {
    LIGHT_LIMIT: LIGHT_LIMIT,
    grid: grid,
    known: known,
    average: average,
    cover: cover,
    inBox: inBox
  };
})();
